import styled from '@emotion/styled';
import { useEffect, useContext } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Flex,
  Image,
  useTheme,
  ButtonLink,
} from '@updater/ui-uds';
import { useProviderRedirect } from 'hooks';
import { useTracking } from 'react-tracking';
import { SingleProviderConfig, CheckoutType } from 'types';
import { OffersContext, MicrositeContext } from 'context';
import { Spinner } from '@phosphor-icons/react';

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
`;
const LoadingContainer = styled.div`
  margin-top: 30px;
  display: flex;
  align-items: center;
  height: 32px;
  .loadingText {
    padding-left: 5px;
  }
`;

// NOTE: the Cross Selling modal experience should only appear
// for single providers.
export function ProviderRedirectModal({
  updatedCheckoutType,
}: {
  updatedCheckoutType: CheckoutType;
}) {
  const offersContext = useContext(OffersContext);
  const providerSummaries = offersContext?.primaryData?.providerSummaries;
  const micrositeConfig = useContext(MicrositeContext);
  const theme = useTheme();
  const { mainProvider, micrositeCode } =
    micrositeConfig as SingleProviderConfig;
  const { primaryData } = offersContext;

  const spectrumProviderSummary = providerSummaries?.find(
    (summary) => summary.provider?.code === 'spectrum'
  );

  const isSpectrumSpecialBulkAgreement =
    micrositeCode === 'spectrum' &&
    spectrumProviderSummary?.provider?.bulkAgreement;

  // A bulk agreement means Spectrum reports itself unavailable yet we still
  // owe the user a redirect, so it forces the redirect posture on regardless
  // of the checkout type the offers resolved to.
  const { redirectEnabled, redirectUrl, redirectOnUnavailable } =
    useProviderRedirect(
      isSpectrumSpecialBulkAgreement
        ? CheckoutType.PROVIDER_REDIRECT
        : updatedCheckoutType
    );

  // bulkAgreement keeps its own tracking
  const redirectsViaUnavailable =
    redirectOnUnavailable && !isSpectrumSpecialBulkAgreement;

  const modalIsOpen =
    redirectEnabled &&
    (isSpectrumSpecialBulkAgreement ||
      redirectOnUnavailable ||
      primaryData.availableOffers?.length > 0);

  const thisProvider =
    primaryData.availableProviders?.[0] ??
    (isSpectrumSpecialBulkAgreement // bulkAgreement won't show 'available' for Spectrum
      ? spectrumProviderSummary?.provider
      : undefined) ??
    (redirectOnUnavailable ? providerSummaries?.[0]?.provider : undefined);

  // TODO: we're getting the logo from serviceability for some reason,
  // even though we have other provider logos hard-coded here.
  // We should pick one or the other...
  const providerLogo = thisProvider ? thisProvider.logo : undefined;

  function closeModal() {}

  const { trackEvent } = useTracking({
    object: 'provider_redirect_modal',
    details: {
      provider: thisProvider ? thisProvider.name : '',
    },
  });

  useEffect(() => {
    if (modalIsOpen) {
      trackEvent({
        verb: 'viewed',
        ...(redirectsViaUnavailable && { details: { reason: 'unavailable' } }),
      });
    }
  }, [modalIsOpen, redirectsViaUnavailable, trackEvent]);

  const navigating = modalIsOpen && redirectUrl !== undefined;

  if (navigating) {
    // Use a timeout here to accommodate cypress, which gets stuck on navigation
    // events and will fail if this fires before it can validate the modal
    // appearing
    setTimeout(() => {
      window.location.assign(redirectUrl);
    }, 500);
  }

  const displayName = mainProvider ? mainProvider.displayName : '';

  return (
    <Modal isOpen={modalIsOpen} setIsOpen={closeModal}>
      <ModalOverlay cursor="default" />
      <ModalContent maxWidth="736" minWidth="1" minHeight="1">
        <ModalBody padding="l" marginBottom="xxxxl">
          <Container>
            {modalIsOpen && (
              <Flex
                flexDirection="row"
                justifyContent="space-around"
                gap={`${theme.space.s}px`}
              >
                {thisProvider && (
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    data-cy="provider-redirect"
                    data-testid="provider-redirect"
                  >
                    <Image
                      key={displayName}
                      src={providerLogo.url}
                      alt={displayName || 'provider logo'}
                      maxWidth="150px"
                    />
                    <LoadingContainer>
                      <Spinner size={32} color="#333" weight="fill">
                        <animateTransform
                          attributeName="transform"
                          attributeType="XML"
                          type="rotate"
                          dur="2s"
                          from="0 0 0"
                          to="360 0 0"
                          repeatCount="indefinite"
                        />
                      </Spinner>
                      <div className="loadingText" data-testid="loadingText">
                        Taking you to {displayName} shopping...
                      </div>
                    </LoadingContainer>
                    {navigating && (
                      <ButtonLink
                        variant="secondary"
                        data-testid="provider-redirect-buttonlink"
                        href={redirectUrl}
                        marginTop="l"
                      >
                        Click here if you are not automatically redirected
                      </ButtonLink>
                    )}
                  </Flex>
                )}
              </Flex>
            )}
          </Container>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
